'use client';

import type { ReferenceCategory } from '@eliadmin/core/src/api/references/references-types';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchReferences } from '@eliadmin/core/src/api/references/references-api';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Heading } from '../ui/heading';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { ReferenceCardSkeleton } from './reference-card-skeleton';
import { ReferenceCard } from './reference-card';
import { Paragraph } from '../ui/paragraph';
import { Button } from '../ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const ALL_TAB_VALUE = '__all__';

export function ReferencesList({
  locale,
  categories,
}: {
  locale: string;
  categories: ReferenceCategory[];
}) {
  const { t } = useTranslations();
  const [activeTab, setActiveTab] = useState<string>(ALL_TAB_VALUE);

  const activeCategoryId = activeTab === ALL_TAB_VALUE ? undefined : Number(activeTab);

  const referencesQuery = useInfiniteQuery({
    queryKey: [CacheKey.REFERENCES, activeCategoryId ?? ALL_TAB_VALUE, locale],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchReferences({
        locale,
        query: {
          page: pageParam,
          results: 2,
          where: activeCategoryId ? { categories: { equals: activeCategoryId } } : undefined,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page) {
        return lastPage?.meta?.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

  const isLoading = referencesQuery.isLoading;

  const references = useMemo(() => {
    const items = referencesQuery.data?.pages.flatMap((page: any) => page.data) ?? [];
    return items;
  }, [referencesQuery.data]);

  return (
    <section
      className={cn(
        'custom-section',
        'section-padding-pt-32 section-padding-pb-16',
        'pr-0 lg:pr-4',
      )}
    >
      <Heading
        title={t('text1763')}
        className={{ container: 'items-center justify-center', title: 'text-center' }}
      />
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className={cn(
          'custom-container',
          'flex flex-col items-center justify-center gap-8 sm:gap-10 lg:gap-12',
        )}
      >
        <TabsList className="hide-scroll-bar flex h-fit w-full flex-nowrap items-center justify-start gap-2 overflow-x-scroll rounded-none bg-transparent p-0 lg:flex-wrap lg:items-center lg:justify-center">
          <TabsTrigger
            value={ALL_TAB_VALUE}
            className={cn(
              'ea-references__tabs-trigger',
              'h-[52px] gap-2 whitespace-nowrap rounded-full px-3 text-base font-medium sm:px-4',
            )}
          >
            {activeTab === ALL_TAB_VALUE && (
              <div className="max-h-2 min-h-2 min-w-2 max-w-2 rounded-full bg-primary" />
            )}
            {t('text1947')}
          </TabsTrigger>
          {categories.map(category => (
            <TabsTrigger
              key={category.id}
              value={String(category.id)}
              className={cn(
                'ea-references__tabs-trigger',
                'h-[52px] gap-2 whitespace-nowrap rounded-full px-3 text-base font-medium sm:px-4',
              )}
            >
              {activeTab === String(category.id) && (
                <div className="max-h-2 min-h-2 min-w-2 max-w-2 rounded-full bg-primary" />
              )}
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent
          value={activeTab}
          className="flex w-full flex-col items-center justify-center gap-6 pr-4 sm:gap-8 md:gap-10 lg:gap-12 lg:pr-0"
        >
          {isLoading ? (
            [...Array(6)].map((_, index) => <ReferenceCardSkeleton key={index} />)
          ) : references.length > 0 ? (
            references.map(reference => (
              <ReferenceCard reference={reference} key={reference.name} />
            ))
          ) : (
            <Paragraph className="w-full pt-12 text-center">
              Žiadne realizácie sa nenašli.
            </Paragraph>
          )}
        </TabsContent>
      </Tabs>
      {referencesQuery.hasNextPage && (
        <div className="flex w-full items-center justify-center pr-4 lg:pr-0">
          <Button
            onClick={() => referencesQuery.fetchNextPage()}
            disabled={referencesQuery.isFetchingNextPage}
            className="w-full sm:w-fit"
          >
            {t('text490')}
          </Button>
        </div>
      )}
    </section>
  );
}
